// Module Page Factory - Creates standardized module pages
// Eliminates the need for boilerplate module page components

import React from 'react';
import GenericModulePage from './GenericModulePage';

/**
 * Factory function that creates a standardized module page component
 * 
 * This eliminates the need to create individual module page files
 * that just wrap GenericModulePage with config.
 * 
 * @param {Object} config - Module configuration (documentConfig, userConfig, etc.)
 * @param {Object} options - Optional customization options
 * @returns {React.Component} - Module page component
 */
export const createModulePage = (config, options = {}) => {
  const {
    displayName = `${config.entityName}Page`,
    description = `${config.entityName} Management System`
  } = options;

  const ModulePage = () => {
    return (
      <GenericModulePage 
        config={config}
      />
    );
  };

  // Set display name for debugging
  ModulePage.displayName = displayName;
  
  // Add metadata for development
  ModulePage.config = config;
  ModulePage.description = description;

  return ModulePage;
};

/**
 * Higher-order component version that wraps GenericModulePage
 * Useful when you need to add module-specific logic around the generic page
 * 
 * @param {Object} config - Module configuration
 * @param {Function} enhancer - Optional function to enhance the component
 * @returns {React.Component} - Enhanced module page component
 */
export const withModulePage = (config, enhancer = null) => {
  return (WrappedComponent = GenericModulePage) => {
    const EnhancedModulePage = (props) => {
      const combinedProps = {
        config,
        ...props
      };

      if (enhancer) {
        return enhancer(<WrappedComponent {...combinedProps} />);
      }

      return <WrappedComponent {...combinedProps} />;
    };

    EnhancedModulePage.displayName = `withModulePage(${config.entityName})`;
    return EnhancedModulePage;
  };
};

/**
 * Creates a module page with custom hooks and lifecycle methods
 * Useful for modules that need additional data fetching or side effects
 * 
 * @param {Object} config - Module configuration
 * @param {Object} hooks - Custom hooks to run { useModuleData, useModuleEffects, etc. }
 * @returns {React.Component} - Module page with custom hooks
 */
export const createModulePageWithHooks = (config, hooks = {}) => {
  const ModulePageWithHooks = (props) => {
    // Run custom hooks if provided
    const customData = hooks.useModuleData ? hooks.useModuleData(config) : {};

    // Merge custom data with base props
    const enhancedConfig = {
      ...config,
      ...customData.config
    };

    return (
      <GenericModulePage 
        config={enhancedConfig}
        {...props}
      />
    );
  };

  ModulePageWithHooks.displayName = `${config.entityName}PageWithHooks`;
  return ModulePageWithHooks;
};

/**
 * Utility to create a module page with specific layout or theming
 * 
 * @param {Object} config - Module configuration
 * @param {Object} layout - Layout configuration { wrapper, theme, className, etc. }
 * @returns {React.Component} - Module page with custom layout
 */
export const createModulePageWithLayout = (config, layout = {}) => {
  const ModulePageWithLayout = (props) => {
    const {
      wrapper: Wrapper = React.Fragment,
      theme,
      className,
      style,
      ...layoutProps
    } = layout;

    const content = (
      <GenericModulePage 
        config={config}
        {...props}
      />
    );

    return (
      <Wrapper {...layoutProps}>
        <div 
          className={className}
          style={style}
          data-theme={theme}
        >
          {content}
        </div>
      </Wrapper>
    );
  };

  ModulePageWithLayout.displayName = `${config.entityName}PageWithLayout`;
  return ModulePageWithLayout;
};

// Default export is the simple factory
export default createModulePage;
